/* eslint-disable max-params */
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { distinctUntilChanged } from 'rxjs';

import {
  WcgButtonComponent,
  WcgDialogComponent,
  WcgInputFieldComponent,
  WcgInputHeight,
  WcgMenuOption,
  WcgUniSelectComponent,
} from '@wcg/frontend-framework';

import {
  IrisMapping,
  IrisSettingsDialogData,
  IrisSettingsForm,
} from '../../../types';
import { noWhitespaceValidator } from '@data-access/shared';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    ReactiveFormsModule,
    WcgDialogComponent,
    WcgUniSelectComponent,
    WcgInputFieldComponent,
    WcgButtonComponent,
  ],
  selector: 'wcg-settings-dialog',
  styleUrl: './iris-settings.dialog.component.css',
  template: `
    <form [formGroup]="irisSettingsForm" novalidate>
      <wcg-dialog title="Settings" [size]="irisSettings.dialogSize">
        <div class="w-full overflow-hidden" content>
          <div class="w-full flex flex-col gap-8">
            <div class="w-full">
              <wcg-uni-select
                class="w-full"
                label="IRIS Routing Group"
                formControlName="routingGroup"
                errorText="IRIS Routing Group is required"
                [options]="irisSettings.routingGroupOptions() || []"
                [required]="true"
                [height]="$inputHeight().Comfortable"
              />
            </div>
            <div class="w-full mb-8">
              @let errorText =
                irisSettingsForm.controls.envelopeType.hasError(
                  'noWhiteSpaceError'
                )
                  ? 'IRIS Envelope Type do not accept empty spaces'
                  : 'IRIS Envelope Type is required';
              <wcg-input-field
                class="w-full"
                label="IRIS Envelope Type"
                formControlName="envelopeType"
                [errorText]="errorText"
                [height]="$inputHeight().Comfortable"
                [isRequired]="true"
                [maxLength]="50"
              />
            </div>
          </div>
        </div>
        <div tertiary>
          <wcg-button
            color="tertiary"
            data-testid="delete-dialog__cancel"
            (click)="cancel()"
          >
            Cancel
          </wcg-button>
        </div>
        <div primary>
          <wcg-button
            data-testid="delete-dialog__confirm"
            color="primary"
            (click)="save()"
            [isDisabled]="!irisSettingsForm.valid"
          >
            Save
          </wcg-button>
        </div>
      </wcg-dialog>
    </form>
  `,
})
export class IrisSettingsDialogComponent implements OnInit {
  public readonly irisSettings =
    inject<IrisSettingsDialogData>(MAT_DIALOG_DATA);
  private readonly _dialogRef = inject(
    MatDialogRef<IrisSettingsDialogComponent>,
  );
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _formBuilder = inject(FormBuilder);

  irisSettingsForm = this._createForm();
  $inputHeight = signal<typeof WcgInputHeight>(WcgInputHeight);

  constructor() {
    this._customListeners();
  }

  ngOnInit() {
    if (this.irisSettings.data()) {
      this._patchIrisSettings();
    }
  }

  save(): void {
    const _irisMapper: IrisMapping = {
      envelope_type: {
        name: this.irisSettingsForm.controls.envelopeType.value!,
      },
      routing_group: {
        id: +this.irisSettingsForm.controls.routingGroup.value!.id!,
        name: this.irisSettingsForm.controls.routingGroup.value!.label!,
      },
    };
    this.irisSettings.callbacks.save(_irisMapper);
  }

  cancel(): void {
    this._dialogRef.close();
  }

  private _createForm() {
    return this._formBuilder.group<IrisSettingsForm>({
      envelopeType: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, noWhitespaceValidator()],
      }),
      routingGroup: new FormControl<WcgMenuOption | null>(null, {
        nonNullable: false,
        validators: [Validators.required],
      }),
    });
  }

  private _patchIrisSettings(): void {
    const irisSettings = this.irisSettings.data();
    if (!irisSettings && !Object.values(irisSettings).length) {
      return;
    }
    this.irisSettingsForm.patchValue(
      {
        envelopeType: irisSettings.envelope_type?.name,
        routingGroup: {
          id: irisSettings.routing_group?.id.toString() ?? '',
          label: irisSettings.routing_group?.name ?? '',
        },
      },
      { emitEvent: false },
    );
  }

  private _customListeners(): void {
    this.irisSettingsForm.controls.routingGroup.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () =>
          this.irisSettingsForm.controls.envelopeType.reset('', {
            emitEvent: false,
          }),
      });

    toObservable(this.irisSettings.canCloseDialog)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (canClose) => {
          if (canClose) {
            this._dialogRef.close();
          }
        },
      });
  }
}
